import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { isAgent, getPendingProfessionalRole } from '../../constants/roles';
import AgentReadyGate from '../agent/onboarding/AgentReadyGate';

/**
 * AgentRoute - the guard for the agent area.
 *
 * Lets agents through, and also users who signed up as an agent and have not
 * confirmed their Agent ID yet: they need /agent/onboarding to get the role, and
 * nothing they can reach from here publishes. Everyone else goes to their own
 * dashboard.
 *
 * Props:
 *  - requireReady : also require a finished onboarding (agent.onboarding.get
 *                   can_enter_dashboard); until then the agent is sent to
 *                   /agent/onboarding. Used for the agent tools.
 */
export const canUseAgentArea = (user) =>
  Boolean(user) && (isAgent(user) || getPendingProfessionalRole(user) === 'agent');

const AgentRoute = ({ children, requireReady = false }) => {
  const { currentUser, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!canUseAgentArea(currentUser)) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  if (requireReady) {
    return <AgentReadyGate>{children}</AgentReadyGate>;
  }

  return children;
};

export default AgentRoute;
